/* ========================================================================
 * Apricot's Floating Menu
 * ======================================================================== */

// SCSS
import '../scss/includes/apricot-base.scss';
import '../scss/includes/floating-menu.scss';

import Utils from './CBUtils';

/**
 * Floating Menu
 * @param {Object} data

 * @param {Element} data.elem
 * @param {String} data.ctaLabel
 * @param {String} data.txtLabel
 * @param {String} data.srTxtLabel
 * @param {Number} data.numActionBtn
 * @param {Number} data.offsetY
 * @param {Number} data.offsetBottom
 * @param {Boolean} data.escClose
 * @param {Number} data.animationTime
 * @param {Boolean} data.animation
 * @param {Boolean} data.useCssTransition
 * @param {Function} data.ctaCallback
 * @param {Function} data.onOpen
 * @param {Function} data.onClose
 * @returns {{updateFloatingMenu: Function}}
 * @returns {{adjustPosition: Function}}
 * @returns {{show: Function}}
 * @returns {{close: Function}}
 * @returns {{reset: Function}}
 * @returns {{destroy: Function}}
 */

const FloatingMenu = (data = {}) => {
  // Check if we're in a browser environment
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return {
      updateFloatingMenu: () => {},
      adjustPosition: () => {},
      show: () => {},
      close: () => {},
      reset: () => {},
      destroy: () => {},
    };
  }

  const defaultData = {
    elem: null,
    ctaLabel: 'Deselect All',
    txtLabel: 'Selected',
    srTxtLabel: `action item(s) available`,
    numActionBtn: 0,
    offsetY: 100,
    offsetBottom: 50,
    escClose: true,
    animationTime: 200,
    animation: true,
    // when true we prefer a simple CSS transition for vertical movement
    // when false we fall back to JS rAF animation (distance-proportional)
    useCssTransition: false,
    ctaCallback: () => {},
    onOpen: () => {},
    onClose: () => {},
  };

  data = {
    ...defaultData,
    ...data,
  };

  let elem = data.elem;
  let triggerElement = null; // Store reference to the element that triggered the menu
  let lastTriggerElement = null; // Store reference to last trigger element
  let activeMenu = null; // Store reference to the currently active menu (mobile or full)
  let topAnimId = null;
  let layoutRafId = null;
  let scrollEndId = null;
  let resizeEndId = null;
  let selectedItems = 0;
  let viewport = Utils.viewport().prefix;

  const debounceDelay = 90; // 90ms feels responsive yet stable

  if (!Utils.elemExists(elem)) return null;

  const menu = elem.querySelector('.cb-floating-menu') || null;
  const txtLabel = menu.querySelector('.cb-floating-menu-txt') || null;
  const ctaLabel = menu.querySelector('.cb-floating-menu-cta') || null;

  const closeBtn = menu.querySelector('.cb-floating-menu-close-btn') || null;
  const mobileBtn = elem.querySelector('.cb-floating-menu-btn') || null;

  const useAnimation = () => {
    return Utils.reduceMotionChanged() ? false : data.animation;
  };

  const measureRect = () => {
    if (!activeMenu) return { width: 0, height: 0 };
    const computed = window.getComputedStyle(activeMenu);
    let rect;
    if (computed.display !== 'none') {
      rect = activeMenu.getBoundingClientRect();
    } else {
      const clone = activeMenu.cloneNode(true);
      clone.style.visibility = 'hidden';

      clone.style.position = 'absolute';

      clone.style.display = 'flex';

      clone.style.left = '-9999px';

      clone.style.top = '-9999px';

      document.body.appendChild(clone);

      rect = clone.getBoundingClientRect();

      document.body.removeChild(clone);
    }

    // on mobile, we need to account for extra height of the Close button
    // but only if we're measuring the full menu (not the button)
    if ((viewport === 'xs' || viewport === 'sm') && !Utils.hasClass(activeMenu, 'cb-floating-menu-btn')) {
      rect.height = rect.height + 88;
    }

    return rect;
  };

  // Smooth vertical animation (up or down) honoring animationTime
  // Uses JS easing for consistent timing regardless of distance; falls back to instant when reduced motion
  const setMenuTop = targetTop => {
    if (!activeMenu) return;

    if (!useAnimation()) {
      activeMenu.style.transition = 'none';

      activeMenu.style.top = `${targetTop}px`;

      return;
    }

    // the user has to provide CSS transition support
    if (data.useCssTransition) {
      activeMenu.style.top = `${targetTop}px`;

      return;
    }

    // Cancel any in-flight JS animation
    if (topAnimId) cancelAnimationFrame(topAnimId);

    // Starting position (parse existing or default to 0)
    const startTop = parseFloat(activeMenu.style.top) || 0;
    const endTop = targetTop;
    const duration = data.animationTime; // ms
    const startTime = performance.now();

    // EaseInOutCubic for smooth acceleration & deceleration
    const easeInOutCubic = t => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

    const step = now => {
      const elapsed = now - startTime;
      const rawProgress = Math.min(elapsed / duration, 1);
      const eased = easeInOutCubic(rawProgress);
      const current = startTop + (endTop - startTop) * eased;
      activeMenu.style.top = `${current}px`;
      if (rawProgress < 1) {
        topAnimId = requestAnimationFrame(step);
      } else {
        activeMenu.style.top = `${endTop}px`;
      }
    };

    topAnimId = requestAnimationFrame(step);
  };
  // position active menu
  // mobile button or full menu
  const positionMenuLayout = () => {
    if (elem.style.display === 'none') return;

    const menuRect = measureRect();

    const left = (window.innerWidth - menuRect.width) / 2;
    // top offsetY from last known triggerElement position or center if none
    let top = data.offsetY;
    if (triggerElement) {
      const triggerRect = triggerElement.getBoundingClientRect();
      const proposedTop = triggerRect.top + data.offsetY;
      const bottomFallback = window.innerHeight - menuRect.height - data.offsetBottom;

      // Use proposed trigger position unless it would push menu out of viewport; then fallback
      if (proposedTop + menuRect.height > window.innerHeight || proposedTop < 0) {
        top = Math.max(0, bottomFallback);
      } else {
        top = proposedTop;
      }
    } else {
      // if we don't have a trigger element, position from bottom (clamped)
      top = Math.max(0, window.innerHeight - menuRect.height - data.offsetBottom);
    }
    if (activeMenu) {
      activeMenu.style.left = `${left}px`;

      activeMenu.style.display = 'flex';
    }

    setMenuTop(top);
  };

  // schedule layout after display/state changes without arbitrary timeout
  const schedulePosition = () => {
    if (layoutRafId) cancelAnimationFrame(layoutRafId);
    // one rAF flushes style changes; second ensures updated metrics if dynamic children appended synchronously
    layoutRafId = requestAnimationFrame(() => {
      layoutRafId = requestAnimationFrame(() => {
        positionMenuLayout();
      });
    });
  };

  // only run if we have a valid menu element
  const checkLayout = vp => {
    const activeViewport = vp || viewport;

    if (selectedItems > 0) {
      elem.style.display = 'block';

      data.onOpen() && data.onOpen();
    }

    if ((activeViewport === 'xs' || activeViewport === 'sm') && mobileBtn.style.display !== 'none') {
      activeMenu = mobileBtn;

      menu.style.display = 'none';

      mobileBtn.style.display = 'flex';
    } else {
      activeMenu = menu;

      mobileBtn.style.display = 'none';

      menu.style.display = 'flex';
    }

    schedulePosition();
  };

  const breakpointChange = e => {
    viewport = e.data.prefix;

    checkLayout(viewport);
  };

  const toggleMobileMenu = () => {
    if (mobileBtn.style.display === 'flex') {
      mobileBtn.style.display = 'none';

      menu.style.display = 'flex';

      activeMenu = menu;
    } else {
      mobileBtn.style.display = 'flex';

      menu.style.display = 'none';

      activeMenu = mobileBtn;
    }

    positionMenuLayout();
  };

  // close menu
  const closeMenu = () => {
    elem.style.display = 'none';

    menu.style.display = 'none';

    mobileBtn.style.display = 'none';

    data.onClose() && data.onClose();
  };

  const adjustSelectedItems = num => {
    let span = null;
    if (num === 1 && data.numActionBtn > 0) {
      span = document.createElement('SPAN');

      const tmp = data.srTxtLabel.replace('(s)', data.numActionBtn === 1 ? '' : 's');
      span.innerHTML = `${data.numActionBtn} ${tmp}`;

      Utils.addClass(span, 'sr-only');

      txtLabel.innerText = `${num} ${data.txtLabel}`;

      txtLabel.appendChild(span);
    } else {
      txtLabel.innerText = `${num} ${data.txtLabel}`;
    }

    if (num === 0) {
      closeMenu();
    }

    // make sure layout is correct after changing selected items
    checkLayout();
  };

  const openMenu = () => {
    if (selectedItems === 0) return;
    elem.style.display = 'block';

    checkLayout(viewport);
  };

  // add or remove button and reposition menu
  const updateFloatingMenu = (trigger, mode) => {
    triggerElement = mode ? trigger : null;

    // we keep it for A11Y focus return
    lastTriggerElement = trigger;

    selectedItems = mode ? selectedItems + 1 : selectedItems - 1 < 0 ? 0 : selectedItems - 1;

    adjustSelectedItems(selectedItems);
  };

  // compute final position after scrolling stops
  const setScrollPosition = () => {
    if (!activeMenu || activeMenu.style.display !== 'flex') return;
    triggerElement = null;
    const menuRect = measureRect();
    const top = Math.max(0, window.innerHeight - menuRect.height - data.offsetBottom);

    setMenuTop(top);
  };

  // debounce scroll end: only reposition after user stops scrolling
  const handleScroll = () => {
    if (scrollEndId) clearTimeout(scrollEndId);
    scrollEndId = setTimeout(setScrollPosition, debounceDelay);
  };

  // debounce resize end: only reposition after user stops resizing
  const handleResize = () => {
    if (resizeEndId) clearTimeout(resizeEndId);
    resizeEndId = setTimeout(positionMenuLayout, debounceDelay);
  };

  // A11Y: close on ESC key
  const closeOnEsc = e => {
    if (data.escClose && e.key === 'Escape' && elem.style.display === 'block') {
      triggerElement = null;

      lastTriggerElement = null;

      closeMenu();
    }
  };

  // close Button clicked
  // xs: switch to mobile button
  // sm and up: close menu
  const closeMobileBtn = e => {
    e.preventDefault();

    // switch to mobile button
    toggleMobileMenu();
  };

  // handle CTA click
  const ctaOnClick = e => {
    e.preventDefault();

    selectedItems = 0;

    adjustSelectedItems(0);

    // close Menu
    closeMenu();
    // if we have a trigger element, focus it back
    if (lastTriggerElement) {
      lastTriggerElement.focus();
    }

    data.ctaCallback && data.ctaCallback(e);
  };

  // Update CTA link label
  const updateCtaLink = () => {
    if (Utils.elemExists(ctaLabel) && data.ctaLabel) {
      if (typeof data.ctaLabel === 'string') {
        ctaLabel.innerText = data.ctaLabel;
      } else {
        ctaLabel.appendChild(data.ctaLabel);
      }
    }
  };

  const resetMenu = () => {
    selectedItems = 0;

    adjustSelectedItems(0);
  };

  const destroy = () => {
    if (elem.floatingMenu === 'cb') {
      elem.floatingMenu = null;
    }

    mobileBtn.removeEventListener('click', toggleMobileMenu);

    closeBtn.removeEventListener('click', closeMobileBtn);

    ctaLabel.removeEventListener('click', ctaOnClick);

    window.removeEventListener('resize', handleResize);
    if (resizeEndId) {
      clearTimeout(resizeEndId);

      resizeEndId = null;
    }

    document.removeEventListener('scroll', handleScroll);
    if (scrollEndId) {
      clearTimeout(scrollEndId);

      scrollEndId = null;
    }

    document.removeEventListener('keydown', closeOnEsc);

    document.removeEventListener('apricot_breakpointChange', breakpointChange);

    txtLabel.innerText = '';

    ctaLabel.innerText = '';

    ctaLabel.removeEventListener('click', ctaOnClick);
    if (topAnimId) cancelAnimationFrame(topAnimId);
    if (layoutRafId) cancelAnimationFrame(layoutRafId);
  };

  const init = () => {
    elem.floatingMenu = 'cb';

    Utils.breakpoints();

    document.addEventListener('apricot_breakpointChange', breakpointChange);

    closeBtn?.addEventListener('click', closeMobileBtn);

    mobileBtn?.addEventListener('click', toggleMobileMenu);

    ctaLabel?.addEventListener('click', ctaOnClick);

    window.addEventListener('resize', handleResize);

    // reposition only after scroll end (debounced)
    document.addEventListener('scroll', handleScroll, { passive: true });

    document.addEventListener('keydown', closeOnEsc);

    // we just call it on init
    updateCtaLink();
  };

  if (elem.floatingMenu !== 'cb') {
    init();
  }

  // FloatingMenu initialized
  return {
    updateFloatingMenu: updateFloatingMenu,
    adjustPosition: positionMenuLayout,
    show: openMenu,
    close: closeMenu,
    reset: resetMenu,
    destroy: destroy,
  };
};

export default FloatingMenu;
